import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import BidsTab from './BidsTab';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [ads, setAds] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoSettings, setPromoSettings] = useState({
    featureEnabled: true,
    defaultDurationMonths: 6,
    promoCodeExpireDays: 30
  });
  const [promoStats, setPromoStats] = useState({
    totalGenerated: 0,
    totalUsed: 0,
    totalActive: 0,
    totalExpired: 0,
    usageRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreatePromoModal, setShowCreatePromoModal] = useState(false);
  const [showCreateSupervisorModal, setShowCreateSupervisorModal] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({
    commissionPercentage: 7.0,
    escrowEnabled: true,
    milestoneThreshold: 500.0,
    autoReleaseAfterDays: 7
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    assignedEmail: '',
    trialDurationDays: 30,
    description: ''
  });
  const [newSupervisor, setNewSupervisor] = useState({
    name: '',
    email: '',
    password: '',
    permissions: ['view_users', 'manage_users', 'manage_promo_codes', 'manage_advertisements', 'view_statistics']
  });
  
  // Edit supervisor state
  const [showEditSupervisorModal, setShowEditSupervisorModal] = useState(false);
  const [editingSupervisor, setEditingSupervisor] = useState(null);
  const [editSupervisorData, setEditSupervisorData] = useState({
    name: '',
    email: '',
    permissions: []
  });
  
  // New state for Terms & Conditions and Encryption management
  const [termsInfo, setTermsInfo] = useState({});
  const [encryptionStatus, setEncryptionStatus] = useState({});
  const [encryptionLoading, setEncryptionLoading] = useState(false);
  
  // Service creation state (moved from ProviderDashboard)
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    category: 'Electrical',
    price: '',
    priceType: 'per hour',
    availability: '',
    responseTime: '',
    features: ''
  });
  const [categories] = useState([
    'Electrical', 'Plumbing', 'HVAC', 'Roofing', 'Carpentry', 'Landscaping',
    'Cleaning', 'Painting', 'Flooring', 'Solar Installation', 'General Repair'
  ]);
  
  // Accounts management state
  const [allAccounts, setAllAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  
  // Page Effects state
  const [pageEffects, setPageEffects] = useState({
    backgroundGradient: {
      from: '#1e40af', // blue-800
      via: '#7c3aed',  // purple-600
      to: '#059669'    // emerald-600
    },
    tabColors: {
      active: '#2563eb',    // blue-600
      inactive: '#374151', // gray-700
      hover: '#1d4ed8'     // blue-700
    },
    textContent: {
      landingTitle: 'Your Complete Professional Solution',
      landingSubtitle: 'Connect with certified professionals across all trades plus licensed engineers and interior designers.',
      dashboardWelcome: 'Welcome to D.C. Technology Corp'
    }
  });
  const [pageEffectsLoading, setPageEffectsLoading] = useState(false);
  const [selectedAccountForId, setSelectedAccountForId] = useState(null);
  const [idVerificationModal, setIdVerificationModal] = useState(false);
  const [idVerificationDetails, setIdVerificationDetails] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(null);

  // Check if user is admin or supervisor
  useEffect(() => {
    if (!user || (user.userType !== 'admin' && user.userType !== 'supervisor')) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const statsData = await response.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast({
        title: "Error",
        description: "Failed to load admin statistics.",
        variant: "destructive",
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAds = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ads`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const adsData = await response.json();
        setAds(adsData);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast({
        title: "Error",
        description: "Failed to load advertisements.",
        variant: "destructive",
      });
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const promoData = await response.json();
        setPromoCodes(promoData);
      }
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      toast({
        title: "Error",
        description: "Failed to load promo codes.",
        variant: "destructive",
      });
    }
  };

  const fetchPromoSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('dcTech_token')}`
        }
      });
      
      if (response.ok) {
        const settings = await response.json();
        setPromoSettings(settings);
      }
    } catch (error) {
      console.error('Error fetching promo settings:', error);
    }
  };

  const fetchPromoStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('dcTech_token')}`
        }
      });
      
      if (response.ok) {
        const stats = await response.json();
        setPromoStats(stats);
      }
    } catch (error) {
      console.error('Error fetching promo stats:', error);
    }
  };

  const updatePromoSettings = async (newSettings) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('dcTech_token')}`
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        await fetchPromoSettings(); // Refresh settings
        toast({
          title: "Settings Updated",
          description: "Promo code settings have been updated successfully.",
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating promo settings:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update promo settings.",
        variant: "destructive",
      });
    }
  };

  const fetchSupervisors = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supervisors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const supervisorData = await response.json();
        setSupervisors(supervisorData);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/activity-logs?hours=24`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const activityData = await response.json();
        setActivityLogs(activityData);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  // Page Effects functions
  const fetchPageEffects = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/page-effects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPageEffects(data);
      } else {
        console.error('Failed to fetch page effects');
      }
    } catch (error) {
      console.error('Error fetching page effects:', error);
    }
  };

  const savePageEffects = async () => {
    try {
      setPageEffectsLoading(true);
      const token = localStorage.getItem('dcTech_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/page-effects`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pageEffects),
      });

      if (response.ok) {
        const result = await response.json();
        setPageEffects(result.effects);
        toast({
          title: "Success",
          description: "Page effects updated successfully",
        });
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to update page effects",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error saving page effects:', error);
      toast({
        title: "Error",
        description: "Network error while saving page effects",
        variant: "destructive",
      });
    } finally {
      setPageEffectsLoading(false);
    }
  };

  const resetPageEffects = async () => {
    try {
      setPageEffectsLoading(true);
      const token = localStorage.getItem('dcTech_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/page-effects/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setPageEffects(result.effects);
        toast({
          title: "Success",
          description: "Page effects reset to defaults successfully",
        });
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to reset page effects",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error resetting page effects:', error);
      toast({
        title: "Error",
        description: "Network error while resetting page effects",
        variant: "destructive",
      });
    } finally {
      setPageEffectsLoading(false);
    }
  };

  // Platform settings functions
  const fetchPlatformSettings = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/platform-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const settingsData = await response.json();
        setPlatformSettings(settingsData);
      }
    } catch (error) {
      console.error('Error fetching platform settings:', error);
    }
  };

  const updatePlatformSettings = async () => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/platform-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(platformSettings),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Platform settings updated successfully",
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to update platform settings",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error updating platform settings:', error);
      toast({
        title: "Error",
        description: "Failed to update platform settings",
        variant: "destructive",
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This will permanently delete the user and all associated data.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "User Deleted",
          description: `${result.deletedUser.name} and all associated data have been permanently deleted.`,
        });
        // Refresh users list
        fetchUsers();
        fetchAdminStats(); // Update stats after deletion
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete user.",
        variant: "destructive",
      });
    }
  };

  const createPromoCode = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPromoCode),
      });

      if (response.ok) {
        toast({
          title: "Promo Code Created",
          description: `Promo code ${newPromoCode.code} created successfully.`,
        });
        setShowCreatePromoModal(false);
        setNewPromoCode({
          code: '',
          assignedEmail: '',
          trialDurationDays: 30,
          description: ''
        });
        fetchPromoCodes(); // Refresh promo codes list
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create promo code');
      }
    } catch (error) {
      console.error('Error creating promo code:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create promo code.",
        variant: "destructive",
      });
    }
  };

  const deletePromoCode = async (codeId, code) => {
    if (!window.confirm(`Are you sure you want to delete promo code "${code}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast({
          title: "Promo Code Deleted",
          description: `Promo code "${code}" has been deleted.`,
        });
        fetchPromoCodes(); // Refresh promo codes list
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete promo code');
      }
    } catch (error) {
      console.error('Error deleting promo code:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete promo code.",
        variant: "destructive",
      });
    }
  };

  const createSupervisor = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supervisors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSupervisor),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Supervisor created successfully",
        });
        setShowCreateSupervisorModal(false);
        setNewSupervisor({
          name: '',
          email: '',
          password: '',
          permissions: ['view_users', 'manage_users', 'manage_promo_codes', 'manage_advertisements', 'view_statistics']
        });
        fetchSupervisors();
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to create supervisor",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error creating supervisor:', error);
      toast({
        title: "Error",
        description: "Failed to create supervisor",
        variant: "destructive",
      });
    }
  };

  // Edit supervisor functions
  const handleEditSupervisor = (supervisor) => {
    setEditingSupervisor(supervisor);
    setEditSupervisorData({
      name: supervisor.name,
      email: supervisor.email,
      permissions: supervisor.supervisorPermissions || []
    });
    setShowEditSupervisorModal(true);
  };

  const updateSupervisor = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supervisors/${editingSupervisor._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editSupervisorData),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Supervisor updated successfully",
        });
        setShowEditSupervisorModal(false);
        setEditingSupervisor(null);
        setEditSupervisorData({
          name: '',
          email: '',
          permissions: []
        });
        fetchSupervisors();
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to update supervisor",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error updating supervisor:', error);
      toast({
        title: "Error",
        description: "Failed to update supervisor",
        variant: "destructive",
      });
    }
  };

  const exportData = async (exportType) => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ exportType }),
      });

      if (response.ok) {
        const exportData = await response.json();
        
        // Create download link
        const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${exportData.downloadUrl}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = exportData.fileName;
        link.setAttribute('style', 'display: none;');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: "Success",
          description: `Exported ${exportData.recordCount} records to ${exportData.fileName}`,
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to export data",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const updateUserStatus = async (userId, statusData) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(statusData),
      });

      if (response.ok) {
        await fetchUsers(); // Refresh users list
        toast({
          title: "Success",
          description: "User status updated successfully.",
        });
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast({
        title: "Error",
        description: "Failed to update user status.",
        variant: "destructive",
      });
    }
  };

  const toggleAdStatus = async (adId, currentStatus) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const ad = ads.find(a => a._id === adId);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ads/${adId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...ad,
          active: !currentStatus,
        }),
      });

      if (response.ok) {
        await fetchAds(); // Refresh ads list
        toast({
          title: "Success",
          description: `Advertisement ${!currentStatus ? 'activated' : 'deactivated'} successfully.`,
        });
      }
    } catch (error) {
      console.error('Error updating ad status:', error);
      toast({
        title: "Error",
        description: "Failed to update advertisement status.",
        variant: "destructive",
      });
    }
  };

  // Service creation function (for admin/supervisor use)
  const createService = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newService,
          features: newService.features.split(',').map(f => f.trim()).filter(f => f),
          price: parseFloat(newService.price),
        }),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Service created successfully!",
        });
        
        // Reset form
        setNewService({
          title: '',
          description: '',
          category: 'Electrical',
          price: '',
          priceType: 'per hour',
          availability: '',
          responseTime: '',
          features: ''
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create service');
      }
    } catch (error) {
      console.error('Error creating service:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create service. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Fetch all accounts
  const fetchAllAccounts = async () => {
    setAccountsLoading(true);
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/all-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const accountsData = await response.json();
        setAllAccounts(accountsData);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast({
        title: "Error",
        description: "Failed to load accounts.",
        variant: "destructive",
      });
    } finally {
      setAccountsLoading(false);
    }
  };

  // Delete account
  const deleteAccount = async (userId, userEmail) => {
    if (!confirm(`Are you sure you want to delete the account for ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/delete-account/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: `Account for ${userEmail} has been deleted.`,
        });
        // Refresh accounts list
        fetchAllAccounts();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete account.",
        variant: "destructive",
      });
    }
  };

  // Reset user password
  const resetUserPassword = async (userId, userEmail, userName) => {
    if (!confirm(`Are you sure you want to reset the password for ${userName} (${userEmail})? They will receive an email with reset instructions.`)) {
      return;
    }

    setResetPasswordLoading(userId);
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-password/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adminAction: true,
          reason: "Password reset requested by admin"
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Password Reset Sent",
          description: `Password reset email sent to ${result.userEmail}`,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password.",
        variant: "destructive",
      });
    } finally {
      setResetPasswordLoading(null);
    }
  };

  // Get ID verification details
  const getIdVerificationDetails = async (userId) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/id-verification-details/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const details = await response.json();
        setIdVerificationDetails(details);
        setSelectedAccountForId(userId);
        setIdVerificationModal(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get ID verification details');
      }
    } catch (error) {
      console.error('Error getting ID verification details:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get ID verification details.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const loadAdminData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAdminStats(),
        fetchUsers(),
        fetchAds(),
        fetchPromoCodes(),
        fetchPromoSettings(),
        fetchPromoStats(),
        fetchSupervisors(),
        fetchActivityLogs(),
        fetchPlatformSettings(),
        fetchPageEffects()
      ]);
      setLoading(false);
    };

    if (user?.userType === 'admin') {
      loadAdminData();
    }
  }, [user]);

  if (!user || (user.userType !== 'admin' && user.userType !== 'supervisor')) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading Admin Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
              <span className="text-red-400 text-sm font-semibold">ADMIN</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-white/80 hidden sm:block">Welcome, {user?.name}</span>
              
              {/* Navigation Dropdown */}
              <div className="relative group">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Navigate
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible  z-50">
                  <div className="py-2">
                    <Link to="/dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🏠 Main Dashboard
                    </Link>
                    {user?.userType === 'admin' && (
                      <Link to="/supervisor-dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        👥 Supervisor View
                      </Link>
                    )}
                    <Link to="/inbox" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      📨 Messages
                    </Link>
                    <Link to="/profile" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      👤 Profile
                    </Link>
                    <div className="border-t border-white/20 my-2"></div>
                    <button 
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-red-400 bg-red-500/20 transition-colors"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Mobile Menu - Simple buttons for mobile */}
              <div className="sm:hidden flex items-center space-x-2">
                <Link to="/dashboard">
                  <Button variant="outline" size="sm" className="border-white/50 text-white bg-white/10">
                    🏠
                  </Button>
                </Link>
                <button 
                  onClick={logout}
                  className="border border-red-400/50 text-red-400 bg-red-500/20 px-2 py-1 rounded text-sm"
                >
                  🚪
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            {user?.userType === 'admin' ? 'Admin Dashboard' : 'Supervisor Dashboard'}
          </h1>
          <p className="text-xl text-white/70">
            Manage users, monitor platform activity, and oversee operations
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">{stats.totalUsers}</div>
                  <div className="text-white/60 text-sm">Total Users</div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">{stats.totalProviders}</div>
                  <div className="text-white/60 text-sm">Providers</div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">{stats.totalJobs}</div>
                  <div className="text-white/60 text-sm">Job Postings</div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">{stats.totalBids}</div>
                  <div className="text-white/60 text-sm">Active Bids</div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-400">{stats.pendingReviews}</div>
                  <div className="text-white/60 text-sm">Pending Reviews</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="grid w-full grid-cols-8 bg-slate-800/80 mb-8">
            <TabsTrigger value="analytics" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              📊 Analytics
            </TabsTrigger>
            <TabsTrigger value="users" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              👥 Users
            </TabsTrigger>
            <TabsTrigger value="supervisors" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              👨‍💼 Supervisors
            </TabsTrigger>
            <TabsTrigger value="promos" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              🎫 Promos
            </TabsTrigger>
            <TabsTrigger value="ads" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              📢 Ads
            </TabsTrigger>
            <TabsTrigger value="data-security" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              🔒 Security
            </TabsTrigger>
            <TabsTrigger value="platform-settings" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              ⚙️ Settings
            </TabsTrigger>
            <TabsTrigger value="page-effects" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              🎨 Page Effects
            </TabsTrigger>
            <TabsTrigger value="promo-settings" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              🎁 Promo Settings
            </TabsTrigger>
          </TabsList>

          {/* Additional tabs row for better organization */}
          <TabsList className="grid w-full grid-cols-6 bg-slate-800/80 mb-8">
            <TabsTrigger value="terms-conditions" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              📜 Terms & Conditions
            </TabsTrigger>
            <TabsTrigger value="encryption" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              🔐 Encryption
            </TabsTrigger>
            <TabsTrigger value="add-service" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              ➕ Add Service
            </TabsTrigger>
            <TabsTrigger value="live-bids" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              💼 Live Bids
            </TabsTrigger>
            <TabsTrigger value="customer-view" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              👤 Customer View
            </TabsTrigger>
            <TabsTrigger value="accounts" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-sm py-2">
              📋 Accounts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Recent Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.slice(0, 10).map(user => (
                    <div key={user._id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <img 
                          src={user.image || '/api/placeholder/40/40'} 
                          alt={user.name}
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <div className="text-white font-medium">{user.name}</div>
                          <div className="text-white/60 text-sm">{user.email}</div>
                          <div className="text-white/40 text-xs">
                            {user.userType} • {user.company || 'No company'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.subscriptionStatus === 'active' 
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {user.subscriptionStatus || 'N/A'}
                        </span>
                        
                        {user.userType === 'provider' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/50 text-white bg-white/10"
                            onClick={() => updateUserStatus(user._id, {
                              subscriptionStatus: user.subscriptionStatus === 'active' ? 'inactive' : 'active'
                            })}
                          >
                            {user.subscriptionStatus === 'active' ? 'Deactivate' : 'Activate'}
                          </Button>
                        )}
                        
                        {/* Delete User Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          onClick={() => deleteUser(user._id, user.name)}
                        >
                          🗑️ Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="promos">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Promo Code Management</CardTitle>
                <Button 
                  onClick={() => setShowCreatePromoModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  ➕ Create Promo Code
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {promoCodes.length === 0 ? (
                    <div className="text-center py-8 text-white/60">
                      No promo codes created yet. Create one to get started!
                    </div>
                  ) : (
                    promoCodes.map(promo => (
                      <div key={promo._id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-medium text-lg">{promo.code}</span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              promo.used 
                                ? 'bg-red-500/20 text-red-400'
                                : promo.active 
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {promo.used ? 'Used' : promo.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="text-white/70 text-sm mt-1">
                            Assigned to: {promo.assignedEmail}
                          </div>
                          <div className="text-white/60 text-sm">
                            {promo.trialDurationDays} days free trial
                            {promo.description && ` • ${promo.description}`}
                          </div>
                          {promo.used && promo.usedAt && (
                            <div className="text-white/50 text-xs mt-1">
                              Used on: {new Date(promo.usedAt).toLocaleDateString()}
                            </div>
                          )}
                          <div className="text-white/40 text-xs mt-1">
                            Created: {new Date(promo.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            onClick={() => deletePromoCode(promo._id, promo.code)}
                          >
                            🗑️ Delete
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ads">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Advertisement Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {ads.map(ad => (
                    <Card key={ad._id} className="bg-white/5 border-white/10">
                      <CardContent className="p-4">
                        <img 
                          src={ad.imageUrl} 
                          alt={ad.title}
                          className="w-full h-32 object-cover rounded mb-3"
                        />
                        <h4 className="text-white font-medium mb-2">{ad.title}</h4>
                        <p className="text-white/70 text-sm mb-3 line-clamp-2">{ad.description}</p>
                        
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-white/60 text-xs">
                            {ad.impressions} impressions • {ad.clicks} clicks
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            ad.active 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {ad.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full border-white/50 text-white bg-white/10"
                          onClick={() => toggleAdStatus(ad._id, ad.active)}
                        >
                          {ad.active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Platform Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-white/60">Total Services:</span>
                      <span className="text-white font-semibold">{stats?.totalServices}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Total Products:</span>
                      <span className="text-white font-semibold">{stats?.totalProducts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Total Orders:</span>
                      <span className="text-white font-semibold">{stats?.totalOrders}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Active Subscriptions:</span>
                      <span className="text-green-400 font-semibold">{stats?.activeSubscriptions}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">User Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-white/60">Customers:</span>
                      <span className="text-blue-400 font-semibold">{stats?.totalCustomers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Providers:</span>
                      <span className="text-green-400 font-semibold">{stats?.totalProviders}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Customer:Provider Ratio:</span>
                      <span className="text-white font-semibold">
                        {stats ? Math.round(stats.totalCustomers / stats.totalProviders * 10) / 10 : 0}:1
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="supervisors">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Supervisor Management</CardTitle>
                <Button 
                  onClick={() => setShowCreateSupervisorModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  ➕ Create Supervisor
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {supervisors.length === 0 ? (
                    <div className="text-center py-8 text-white/60">
                      No supervisors created yet. Create one to get started!
                    </div>
                  ) : (
                    supervisors.map(supervisor => (
                      <div key={supervisor._id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold">
                              {supervisor.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-white font-medium">{supervisor.name}</div>
                            <div className="text-white/60 text-sm">{supervisor.email}</div>
                            <div className="text-white/40 text-xs">
                              {supervisor.permissions?.length || 0} permissions
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            supervisor.active 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {supervisor.active ? 'Active' : 'Inactive'}
                          </span>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditSupervisor(supervisor)}
                            className="border-white/50 text-white bg-white/10"
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="data-security">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Data Export</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-white/70 text-sm mb-4">
                    Export platform data for backup, analysis, or compliance purposes.
                  </div>
                  
                  <Button
                    onClick={() => exportData('users')}
                    disabled={exportLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {exportLoading ? 'Exporting...' : '📊 Export User Data'}
                  </Button>
                  
                  <Button
                    onClick={() => exportData('jobs')}
                    disabled={exportLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    {exportLoading ? 'Exporting...' : '💼 Export Job Data'}
                  </Button>
                  
                  <Button
                    onClick={() => exportData('transactions')}
                    disabled={exportLoading}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {exportLoading ? 'Exporting...' : '💳 Export Transaction Data'}
                  </Button>
                  
                  <Button
                    onClick={() => exportData('all')}
                    disabled={exportLoading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    {exportLoading ? 'Exporting...' : '🗂️ Export All Data'}
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Recent Activity Logs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {activityLogs.length === 0 ? (
                      <div className="text-center py-8 text-white/60">
                        No recent activity logs available.
                      </div>
                    ) : (
                      activityLogs.slice(0, 20).map((log, index) => (
                        <div key={index} className="p-3 bg-white/5 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-white text-sm font-medium">
                                {log.action}
                              </div>
                              <div className="text-white/60 text-xs">
                                User: {log.userName || log.userEmail || 'System'}
                              </div>
                              {log.details && (
                                <div className="text-white/50 text-xs mt-1">
                                  {log.details}
                                </div>
                              )}
                            </div>
                            <div className="text-white/40 text-xs">
                              {new Date(log.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="platform-settings">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Commission & Payment Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Platform Commission (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        step="0.1"
                        value={platformSettings.commissionPercentage}
                        onChange={(e) => setPlatformSettings({
                          ...platformSettings,
                          commissionPercentage: parseFloat(e.target.value) || 0
                        })}
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="7.0"
                      />
                      <p className="text-white/60 text-sm mt-1">Current: {platformSettings.commissionPercentage}%</p>
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">
                        Milestone Threshold ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="10"
                        value={platformSettings.milestoneThreshold}
                        onChange={(e) => setPlatformSettings({
                          ...platformSettings,
                          milestoneThreshold: parseFloat(e.target.value) || 0
                        })}
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="500"
                      />
                      <p className="text-white/60 text-sm mt-1">
                        Jobs above this amount can use milestone payments
                      </p>
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">
                        Auto Release After (Days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={platformSettings.autoReleaseAfterDays}
                        onChange={(e) => setPlatformSettings({
                          ...platformSettings,
                          autoReleaseAfterDays: parseInt(e.target.value) || 7
                        })}
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="7"
                      />
                      <p className="text-white/60 text-sm mt-1">
                        Automatically release escrow after completion
                      </p>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="escrowEnabled"
                        checked={platformSettings.escrowEnabled}
                        onChange={(e) => setPlatformSettings({
                          ...platformSettings,
                          escrowEnabled: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 bg-white/10 border-white/20 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="escrowEnabled" className="text-white font-medium">
                        Enable Escrow System
                      </label>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/20">
                    <button
                      onClick={updatePlatformSettings}
                      disabled={settingsLoading}
                      className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded transition-colors"
                    >
                      {settingsLoading ? 'Updating...' : 'Save Settings'}
                    </button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Current Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">Commission Rate</span>
                        <span className="text-green-400 font-bold">{platformSettings.commissionPercentage}%</span>
                      </div>
                      <p className="text-white/60 text-sm mt-1">
                        Revenue per transaction: ${((100 * platformSettings.commissionPercentage) / 100).toFixed(2)} per $100
                      </p>
                    </div>

                    <div className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">Payment Models</span>
                        <div className="text-right">
                          <div className="text-blue-400 text-sm">Under ${platformSettings.milestoneThreshold}: 50/50</div>
                          <div className="text-purple-400 text-sm">Over ${platformSettings.milestoneThreshold}: Milestones</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">Escrow Status</span>
                        <span className={`font-bold ${platformSettings.escrowEnabled ? 'text-green-400' : 'text-red-400'}`}>
                          {platformSettings.escrowEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-white/60 text-sm mt-1">
                        Auto-release: {platformSettings.autoReleaseAfterDays} days after completion
                      </p>
                    </div>

                    <div className="bg-white/5 p-4 rounded-lg">
                      <h3 className="text-white font-semibold mb-2">Recent Changes</h3>
                      <p className="text-white/60 text-sm">
                        Last updated: {new Date().toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Provider Signup Promo Settings */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">🎁 Provider Signup Promo Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 p-4 rounded-lg border border-blue-500/30">
                      <div className="text-blue-400 text-sm">Generated</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalGenerated}</div>
                    </div>
                    <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 p-4 rounded-lg border border-green-500/30">
                      <div className="text-green-400 text-sm">Used</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalUsed}</div>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 p-4 rounded-lg border border-yellow-500/30">
                      <div className="text-yellow-400 text-sm">Active</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalActive}</div>
                    </div>
                    <div className="bg-gradient-to-r from-red-500/20 to-red-600/20 p-4 rounded-lg border border-red-500/30">
                      <div className="text-red-400 text-sm">Expired</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalExpired}</div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 p-4 rounded-lg border border-purple-500/30">
                      <div className="text-purple-400 text-sm">Usage Rate</div>
                      <div className="text-white text-2xl font-bold">{promoStats.usageRate}%</div>
                    </div>
                  </div>

                  {/* Settings Form */}
                  <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-white font-medium">Enable Promo Feature</label>
                          <p className="text-white/60 text-sm">Toggle the provider signup promo code feature on/off</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={promoSettings.featureEnabled}
                            onChange={(e) => updatePromoSettings({ 
                              ...promoSettings, 
                              featureEnabled: e.target.checked 
                            })}
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={`text-sm font-medium ${
                            promoSettings.featureEnabled ? 'text-green-400' : 'text-gray-400'
                          }`}>
                            {promoSettings.featureEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-white font-medium">Free Plan Duration</label>
                          <p className="text-white/60 text-sm mb-2">Default months of free Basic plan access</p>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={promoSettings.defaultDurationMonths}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value);
                              if (newValue >= 1 && newValue <= 24) {
                                updatePromoSettings({
                                  ...promoSettings,
                                  defaultDurationMonths: newValue
                                });
                              }
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-md text-white"
                          />
                        </div>

                        <div>
                          <label className="text-white font-medium">Promo Code Expiry</label>
                          <p className="text-white/60 text-sm mb-2">Days until promo code expires</p>
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={promoSettings.promoCodeExpireDays}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value);
                              if (newValue >= 1 && newValue <= 365) {
                                updatePromoSettings({
                                  ...promoSettings,
                                  promoCodeExpireDays: newValue
                                });
                              }
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-md text-white"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 pt-4">
                        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                          promoSettings.featureEnabled 
                            ? 'bg-green-500/20 border border-green-500/30' 
                            : 'bg-gray-500/20 border border-gray-500/30'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            promoSettings.featureEnabled ? 'bg-green-400' : 'bg-gray-400'
                          }`}></div>
                          <span className="text-white text-sm">
                            {promoSettings.featureEnabled 
                              ? `Feature Active - ${promoSettings.defaultDurationMonths} months free` 
                              : 'Feature Disabled'}
                          </span>
                        </div>
                        
                        <Button
                          onClick={() => {
                            fetchPromoStats();
                            fetchPromoSettings();
                          }}
                          variant="outline"
                          size="sm"
                          className="border-white/50 text-white bg-white/10"
                        >
                          🔄 Refresh Stats
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Provider Signup Promo Settings Tab Content */}
          <TabsContent value="promo-settings">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">🎁 Provider Signup Promo Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 p-4 rounded-lg border border-blue-500/30">
                      <div className="text-blue-400 text-sm">Generated</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalGenerated}</div>
                    </div>
                    <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 p-4 rounded-lg border border-green-500/30">
                      <div className="text-green-400 text-sm">Used</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalUsed}</div>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 p-4 rounded-lg border border-yellow-500/30">
                      <div className="text-yellow-400 text-sm">Active</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalActive}</div>
                    </div>
                    <div className="bg-gradient-to-r from-red-500/20 to-red-600/20 p-4 rounded-lg border border-red-500/30">
                      <div className="text-red-400 text-sm">Expired</div>
                      <div className="text-white text-2xl font-bold">{promoStats.totalExpired}</div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 p-4 rounded-lg border border-purple-500/30">
                      <div className="text-purple-400 text-sm">Usage Rate</div>
                      <div className="text-white text-2xl font-bold">{promoStats.usageRate}%</div>
                    </div>
                  </div>

                  {/* Settings Form */}
                  <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-white font-medium">Enable Promo Feature</label>
                          <p className="text-white/60 text-sm">Toggle the provider signup promo code feature on/off</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={promoSettings.featureEnabled}
                            onChange={(e) => updatePromoSettings({ 
                              ...promoSettings, 
                              featureEnabled: e.target.checked 
                            })}
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={`text-sm font-medium ${
                            promoSettings.featureEnabled ? 'text-green-400' : 'text-gray-400'
                          }`}>
                            {promoSettings.featureEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-white font-medium">Free Plan Duration</label>
                          <p className="text-white/60 text-sm mb-2">Default months of free Basic plan access</p>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={promoSettings.defaultDurationMonths}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value);
                              if (newValue >= 1 && newValue <= 24) {
                                updatePromoSettings({
                                  ...promoSettings,
                                  defaultDurationMonths: newValue
                                });
                              }
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-md text-white"
                          />
                        </div>

                        <div>
                          <label className="text-white font-medium">Promo Code Expiry</label>
                          <p className="text-white/60 text-sm mb-2">Days until promo code expires</p>
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={promoSettings.promoCodeExpireDays}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value);
                              if (newValue >= 1 && newValue <= 365) {
                                updatePromoSettings({
                                  ...promoSettings,
                                  promoCodeExpireDays: newValue
                                });
                              }
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-md text-white"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 pt-4">
                        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                          promoSettings.featureEnabled 
                            ? 'bg-green-500/20 border border-green-500/30' 
                            : 'bg-gray-500/20 border border-gray-500/30'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            promoSettings.featureEnabled ? 'bg-green-400' : 'bg-gray-400'
                          }`}></div>
                          <span className="text-white text-sm">
                            {promoSettings.featureEnabled 
                              ? `Feature Active - ${promoSettings.defaultDurationMonths} months free` 
                              : 'Feature Disabled'}
                          </span>
                        </div>
                        
                        <Button
                          onClick={() => {
                            fetchPromoStats();
                            fetchPromoSettings();
                          }}
                          variant="outline"
                          size="sm"
                          className="border-white/50 text-white bg-white/10"
                        >
                          🔄 Refresh Stats
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Terms and Conditions Management */}
          <TabsContent value="terms-conditions">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Terms & Conditions Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">Current Version</span>
                        <span className="text-blue-400 font-bold">{termsInfo.currentVersion || '1.0'}</span>
                      </div>
                      <p className="text-white/60 text-sm">
                        Last Updated: {termsInfo.lastUpdated ? new Date(termsInfo.lastUpdated).toLocaleDateString() : 'Today'}
                      </p>
                    </div>

                    <div className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">User Acceptance</span>
                        <span className="text-green-400 font-bold">{termsInfo.acceptedTerms || 0}/{termsInfo.totalUsers || 0}</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{width: `${termsInfo.totalUsers ? (termsInfo.acceptedTerms / termsInfo.totalUsers) * 100 : 0}%`}}
                        ></div>
                      </div>
                    </div>

                    <div className="bg-yellow-600/20 border border-yellow-400/30 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-yellow-200 font-medium">Pending Acceptance</span>
                        <span className="text-yellow-400 font-bold">{termsInfo.pendingAcceptance || 0}</span>
                      </div>
                      <p className="text-yellow-200/80 text-sm">
                        Users who haven't accepted latest terms
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Terms Management Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <button
                      onClick={() => window.open('/terms-and-conditions', '_blank')}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded transition-colors"
                    >
                      📜 View Current Terms
                    </button>

                    <button
                      onClick={() => {
                        const newVersion = prompt('Enter new Terms version (e.g., 1.1):');
                        if (newVersion) {
                          // updateTermsVersion(newVersion);
                          toast({
                            title: "Terms Updated",
                            description: `Terms version updated to ${newVersion}. All users will need to re-accept.`,
                          });
                        }
                      }}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded transition-colors"
                    >
                      🔄 Update Terms Version
                    </button>

                    <div className="bg-red-600/20 border border-red-400/30 rounded-lg p-4">
                      <p className="text-red-200 text-sm mb-2">
                        <strong>⚠️ Important:</strong> Updating terms version will require all users to re-accept terms before using the platform.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Encryption Management */}
          <TabsContent value="encryption">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Encryption System Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="bg-green-600/20 border border-green-400/30 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                        <span className="text-green-200 font-medium">System Active</span>
                      </div>
                      <p className="text-green-200/80 text-sm">
                        Advanced AES-256 encryption is operational
                      </p>
                    </div>

                    <div className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">Encrypted Files</span>
                        <span className="text-blue-400 font-bold">{encryptionStatus.encryptedFilesCount || 0}</span>
                      </div>
                      <p className="text-white/60 text-sm">
                        Application scripts and sensitive data
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-white font-medium">Encryption Keys Status</h4>
                      {Object.entries(encryptionStatus.keyStatus || {}).map(([keyType, status]) => (
                        <div key={keyType} className="flex justify-between items-center bg-white/5 p-2 rounded">
                          <span className="text-white/80 text-sm capitalize">{keyType.replace('_', ' ')}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            status === 'active' ? 'bg-green-600 text-green-100' : 'bg-red-600 text-red-100'
                          }`}>
                            {status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">Security Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setEncryptionLoading(true);
                        // Mock encryption process
                        setTimeout(() => {
                          setEncryptionLoading(false);
                          toast({
                            title: "Scripts Encrypted",
                            description: "Application scripts have been encrypted successfully.",
                          });
                        }, 3000);
                      }}
                      disabled={encryptionLoading}
                      className="w-full bg-purple-500 hover:bg-purple-600 disabled:bg-gray-500 text-white font-semibold py-3 px-4 rounded transition-colors"
                    >
                      {encryptionLoading ? '🔄 Encrypting...' : '🔐 Encrypt All Scripts'}
                    </button>

                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to rotate all encryption keys? This is a security-sensitive operation.')) {
                          toast({
                            title: "Keys Rotated",
                            description: "All encryption keys have been rotated successfully.",
                          });
                        }
                      }}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded transition-colors"
                    >
                      🔄 Rotate Encryption Keys
                    </button>

                    <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4">
                      <h4 className="text-blue-200 font-medium mb-2">Security Levels</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-200/80">Personal Data:</span>
                          <span className="text-blue-300">HIGH</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-200/80">ID Documents:</span>
                          <span className="text-blue-300">MAXIMUM</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-200/80">Payment Info:</span>
                          <span className="text-blue-300">MAXIMUM</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-200/80">Communications:</span>
                          <span className="text-blue-300">HIGH</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-600/20 border border-red-400/30 rounded-lg p-4">
                      <p className="text-red-200 text-sm">
                        <strong>⚠️ Security Note:</strong> All sensitive data is encrypted using AES-256 with PBKDF2 key derivation and secure salting.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Add Service Management - Admin/Supervisor Only */}
          <TabsContent value="add-service">
            <div className="max-w-4xl mx-auto">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">Create New Service</CardTitle>
                  <p className="text-white/70">Add a new service to the platform (Admin/Supervisor only)</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createService} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="title" className="text-white font-medium">Service Title</label>
                        <input
                          id="title"
                          type="text"
                          value={newService.title}
                          onChange={(e) => setNewService({...newService, title: e.target.value})}
                          className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50"
                          placeholder="e.g., Residential Electrical Installation"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="category" className="text-white font-medium">Category</label>
                        <select
                          id="category"
                          value={newService.category}
                          onChange={(e) => setNewService({...newService, category: e.target.value})}
                          className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white"
                          required
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat} className="bg-slate-800">{cat}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="price" className="text-white font-medium">Price</label>
                        <input
                          id="price"
                          type="number"
                          step="0.01"
                          value={newService.price}
                          onChange={(e) => setNewService({...newService, price: e.target.value})}
                          className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50"
                          placeholder="85.00"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="priceType" className="text-white font-medium">Price Type</label>
                        <select
                          id="priceType"
                          value={newService.priceType}
                          onChange={(e) => setNewService({...newService, priceType: e.target.value})}
                          className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white"
                        >
                          <option value="per hour" className="bg-slate-800">per hour</option>
                          <option value="per job" className="bg-slate-800">per job</option>
                          <option value="per visit" className="bg-slate-800">per visit</option>
                          <option value="per sq ft" className="bg-slate-800">per sq ft</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="availability" className="text-white font-medium">Availability</label>
                        <input
                          id="availability"
                          type="text"
                          value={newService.availability}
                          onChange={(e) => setNewService({...newService, availability: e.target.value})}
                          className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50"
                          placeholder="e.g., Mon-Fri 8AM-6PM"
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="responseTime" className="text-white font-medium">Response Time</label>
                        <input
                          id="responseTime"
                          type="text"
                          value={newService.responseTime}
                          onChange={(e) => setNewService({...newService, responseTime: e.target.value})}
                          className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50"
                          placeholder="e.g., Within 2 hours"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="description" className="text-white font-medium">Description</label>
                      <textarea
                        id="description"
                        value={newService.description}
                        onChange={(e) => setNewService({...newService, description: e.target.value})}
                        className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50 resize-none"
                        rows="4"
                        placeholder="Describe the service in detail..."
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="features" className="text-white font-medium">Features (comma-separated)</label>
                      <input
                        id="features"
                        type="text"
                        value={newService.features}
                        onChange={(e) => setNewService({...newService, features: e.target.value})}
                        className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50"
                        placeholder="e.g., Licensed, Insured, 24/7 Emergency Service"
                      />
                    </div>

                    <div className="flex justify-end space-x-4">
                      <button
                        type="button"
                        onClick={() => setNewService({
                          title: '',
                          description: '',
                          category: 'Electrical',
                          price: '',
                          priceType: 'per hour',
                          availability: '',
                          responseTime: '',
                          features: ''
                        })}
                        className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        Reset Form
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 bg-gradient-to-r from-green-500 to-blue-500   text-white rounded-lg transition-colors"
                      >
                        Create Service
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Customer Dashboard View */}
          <TabsContent value="customer-view">
            <div className="max-w-6xl mx-auto">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">Customer Dashboard View</CardTitle>
                  <p className="text-white/70">Experience the platform from a customer's perspective</p>
                </CardHeader>
                <CardContent>
                  <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-6 mb-6">
                    <h3 className="text-blue-300 text-xl font-semibold mb-4">Customer Experience Preview</h3>
                    <p className="text-blue-200 mb-4">
                      This simulates what customers see when they access the platform. Use this to understand user experience and identify areas for improvement.
                    </p>
                    <div className="flex space-x-4">
                      <a 
                        href="/dashboard" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                      >
                        🏠 View Customer Dashboard
                      </a>
                      <a 
                        href="/subscription" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                      >
                        💳 View Subscription Page
                      </a>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-white/5 border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white text-lg">Customer Features</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 text-white/80">
                          <li>• Browse available services</li>
                          <li>• View products and equipment</li>
                          <li>• Access live job bids</li>
                          <li>• Post new job requests</li>
                          <li>• Manage bookings and payments</li>
                          <li>• Leave reviews and ratings</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/5 border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white text-lg">Admin Notes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 text-white/80 text-sm">
                          <li>• Monitor customer journey flow</li>
                          <li>• Test new features from user perspective</li>
                          <li>• Identify UI/UX improvements</li>
                          <li>• Verify payment and booking processes</li>
                          <li>• Check mobile responsiveness</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* All Accounts Management */}
          <TabsContent value="accounts">
            <div className="max-w-7xl mx-auto">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white text-2xl">All User Accounts</CardTitle>
                    <p className="text-white/70">Manage all registered users with creation timestamps</p>
                  </div>
                  <Button
                    onClick={fetchAllAccounts}
                    disabled={accountsLoading}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {accountsLoading ? 'Loading...' : 'Refresh Accounts'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {accountsLoading ? (
                    <div className="text-center py-8">
                      <div className="text-white">Loading accounts...</div>
                    </div>
                  ) : allAccounts.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-white/20">
                            <th className="text-white font-semibold p-3">Name</th>
                            <th className="text-white font-semibold p-3">Email</th>
                            <th className="text-white font-semibold p-3">Type</th>
                            <th className="text-white font-semibold p-3">Company</th>
                            <th className="text-white font-semibold p-3">ID Verification</th>
                            <th className="text-white font-semibold p-3">Subscription Status</th>
                            <th className="text-white font-semibold p-3">Created</th>
                            <th className="text-white font-semibold p-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allAccounts.map((account) => (
                            <tr key={account._id} className="border-b border-white/10 hover:bg-white/5">
                              <td className="text-white/90 p-3">{account.name}</td>
                              <td className="text-white/90 p-3">{account.email}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  account.userType === 'admin' ? 'bg-red-600/20 text-red-300' :
                                  account.userType === 'supervisor' ? 'bg-yellow-600/20 text-yellow-300' :
                                  account.userType === 'provider' ? 'bg-blue-600/20 text-blue-300' :
                                  'bg-green-600/20 text-green-300'
                                }`}>
                                  {account.userType}
                                </span>
                              </td>
                              <td className="text-white/90 p-3">{account.company || 'N/A'}</td>
                              <td className="p-3">
                                {account.idVerification ? (
                                  <div className="space-y-1">
                                    {account.idVerification.documentUploaded ? (
                                      <div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          account.idVerification.nameVerificationStatus === 'verified' 
                                            ? 'bg-green-600/20 text-green-300' 
                                            : account.idVerification.nameVerificationStatus === 'partial_match'
                                            ? 'bg-yellow-600/20 text-yellow-300'
                                            : account.idVerification.nameVerificationStatus === 'no_match'
                                            ? 'bg-red-600/20 text-red-300'
                                            : 'bg-gray-600/20 text-gray-300'
                                        }`}>
                                          {account.idVerification.nameVerificationStatus === 'verified' ? '✅ Verified' :
                                           account.idVerification.nameVerificationStatus === 'partial_match' ? '⚠️ Partial Match' :
                                           account.idVerification.nameVerificationStatus === 'no_match' ? '❌ No Match' :
                                           '⏳ Pending'}
                                        </span>
                                        {account.idVerification.nameVerificationScore > 0 && (
                                          <div className="text-xs text-white/70 mt-1">
                                            Score: {account.idVerification.nameVerificationScore.toFixed(1)}%
                                          </div>
                                        )}
                                        <div className="mt-1">
                                          <Button
                                            onClick={() => getIdVerificationDetails(account._id)}
                                            variant="outline"
                                            size="sm"
                                            className="text-xs bg-blue-600/20 text-blue-300 border-blue-400/30 hover:bg-blue-600/30"
                                          >
                                            View Details
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-gray-600/20 text-gray-300">
                                        No ID Uploaded
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-white/60 text-xs">Loading...</span>
                                )}
                              </td>
                              <td className="p-3">
                                {account.subscriptionInfo ? (
                                  <div className="space-y-1">
                                    {account.subscriptionInfo.status === 'not_applicable' ? (
                                      <span className="text-white/60 text-xs">Not Applicable</span>
                                    ) : account.subscriptionInfo.status === 'active' ? (
                                      <div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          account.subscriptionInfo.plan === 'free_trial' 
                                            ? 'bg-green-600/20 text-green-300' 
                                            : 'bg-blue-600/20 text-blue-300'
                                        }`}>
                                          {account.subscriptionInfo.planDisplayName}
                                        </span>
                                        {account.subscriptionInfo.daysRemaining !== undefined && (
                                          <div className="text-xs text-white/70 mt-1">
                                            {account.subscriptionInfo.plan === 'free_trial' 
                                              ? `${account.subscriptionInfo.daysRemaining} days left`
                                              : `Expires in ${account.subscriptionInfo.daysRemaining} days`
                                            }
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-red-600/20 text-red-300">
                                        No Active Subscription
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-white/60 text-xs">Loading...</span>
                                )}
                              </td>
                              <td className="text-white/90 p-3">
                                {account.createdAt ? new Date(account.createdAt).toLocaleString() : 'Unknown'}
                              </td>
                              <td className="p-3">
                                <div className="flex flex-col space-y-2">
                                  {account.userType !== 'admin' && (
                                    <>
                                      <Button
                                        onClick={() => resetUserPassword(account._id, account.email, account.name)}
                                        disabled={resetPasswordLoading === account._id}
                                        variant="outline"
                                        size="sm"
                                        className="bg-orange-600/20 text-orange-300 border-orange-400/30 hover:bg-orange-600/30"
                                      >
                                        {resetPasswordLoading === account._id ? 'Sending...' : '🔑 Reset Password'}
                                      </Button>
                                      <Button
                                        onClick={() => deleteAccount(account._id, account.email)}
                                        variant="destructive"
                                        size="sm"
                                        className="bg-red-600 hover:bg-red-700 text-white"
                                      >
                                        Delete
                                      </Button>
                                    </>
                                  )}
                                  {account.userType === 'admin' && (
                                    <span className="text-gray-400 text-xs">Protected Account</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-white/60">No accounts found. Click "Refresh Accounts" to load data.</div>
                    </div>
                  )}

                  {allAccounts.length > 0 && (
                    <div className="mt-6 p-4 bg-yellow-600/20 border border-yellow-400/30 rounded-lg">
                      <p className="text-yellow-200 text-sm">
                        <strong>⚠️ Warning:</strong> Deleting an account is permanent and cannot be undone. 
                        Admin accounts are protected and cannot be deleted. Use this feature with caution.
                      </p>
                    </div>
                  )}

                  {/* ID Verification Details Modal */}
                  {idVerificationModal && idVerificationDetails && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-bold text-gray-900">ID Verification Details</h3>
                          <button
                            onClick={() => {
                              setIdVerificationModal(false);
                              setIdVerificationDetails(null);
                              setSelectedAccountForId(null);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>
                        
                        <div className="space-y-4">
                          {/* User Info */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">User Information</h4>
                            <p><strong>Name:</strong> {idVerificationDetails.userName}</p>
                            <p><strong>Email:</strong> {idVerificationDetails.userEmail}</p>
                          </div>

                          {/* ID Document */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">ID Document</h4>
                            {idVerificationDetails.idVerification.documentUploaded ? (
                              <div>
                                <p className="mb-2"><strong>Status:</strong> Document Uploaded</p>
                                {idVerificationDetails.idVerification.documentUrl && (
                                  <div className="mb-2">
                                    <img 
                                      src={`${process.env.REACT_APP_BACKEND_URL}${idVerificationDetails.idVerification.documentUrl}`}
                                      alt="ID Document"
                                      className="max-w-full h-auto border rounded"
                                      style={{ maxHeight: '300px' }}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-600">No ID document uploaded</p>
                            )}
                          </div>

                          {/* Name Verification */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Name Verification</h4>
                            <div className="space-y-2">
                              <p><strong>Profile Name:</strong> {idVerificationDetails.idVerification.profileName}</p>
                              <p><strong>Verification Status:</strong> 
                                <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${
                                  idVerificationDetails.idVerification.nameVerificationStatus === 'verified' 
                                    ? 'bg-green-100 text-green-800' 
                                    : idVerificationDetails.idVerification.nameVerificationStatus === 'partial_match'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : idVerificationDetails.idVerification.nameVerificationStatus === 'no_match'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {idVerificationDetails.idVerification.nameVerificationStatus === 'verified' ? '✅ Verified' :
                                   idVerificationDetails.idVerification.nameVerificationStatus === 'partial_match' ? '⚠️ Partial Match' :
                                   idVerificationDetails.idVerification.nameVerificationStatus === 'no_match' ? '❌ No Match - Manual Review Required' :
                                   '⏳ Pending'}
                                </span>
                              </p>
                              {idVerificationDetails.idVerification.nameVerificationScore > 0 && (
                                <p><strong>Confidence Score:</strong> {idVerificationDetails.idVerification.nameVerificationScore.toFixed(1)}%</p>
                              )}
                              {idVerificationDetails.idVerification.verifiedAt && (
                                <p><strong>Verified At:</strong> {new Date(idVerificationDetails.idVerification.verifiedAt).toLocaleString()}</p>
                              )}
                              {idVerificationDetails.idVerification.hasNameMismatch && (
                                <div className="bg-red-50 border border-red-200 rounded p-3 mt-2">
                                  <p className="text-red-800 text-sm">
                                    <strong>⚠️ Attention Required:</strong> There is a mismatch between the profile name and the name extracted from the ID document. Manual review is recommended.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Administrative Actions</h4>
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => resetUserPassword(idVerificationDetails.userId, idVerificationDetails.userEmail, idVerificationDetails.userName)}
                                disabled={resetPasswordLoading === idVerificationDetails.userId}
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                              >
                                {resetPasswordLoading === idVerificationDetails.userId ? 'Sending...' : '🔑 Reset Password'}
                              </Button>
                              {idVerificationDetails.idVerification.hasNameMismatch && (
                                <Button
                                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                                  onClick={() => {
                                    toast({
                                      title: "Manual Review",
                                      description: "Flag this account for manual review due to name mismatch.",
                                    });
                                  }}
                                >
                                  🔍 Flag for Review
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 flex justify-end">
                          <Button
                            onClick={() => {
                              setIdVerificationModal(false);
                              setIdVerificationDetails(null);
                              setSelectedAccountForId(null);
                            }}
                            variant="outline"
                          >
                            Close
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Live Bids Tab for Admin/Supervisor */}
          <TabsContent value="live-bids">
            <div className="max-w-7xl mx-auto">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">Live Bids Overview</CardTitle>
                  <p className="text-white/70">Monitor all active bids and job postings across the platform</p>
                </CardHeader>
                <CardContent>
                  <BidsTab 
                    searchTerm=""
                    selectedCategory="all"
                    user={user}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Page Effects Tab */}
          <TabsContent value="page-effects">
            <div className="space-y-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">🎨 Page Appearance Settings</CardTitle>
                  <CardDescription className="text-white/70">
                    Customize the visual appearance of your platform
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Background Colors */}
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold text-lg">Background Gradient</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">From Color</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={pageEffects.backgroundGradient.from}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              backgroundGradient: {
                                ...prev.backgroundGradient,
                                from: e.target.value
                              }
                            }))}
                            className="w-12 h-8 rounded border border-white/20 bg-transparent"
                          />
                          <Input
                            type="text"
                            value={pageEffects.backgroundGradient.from}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              backgroundGradient: {
                                ...prev.backgroundGradient,
                                from: e.target.value
                              }
                            }))}
                            className="bg-white/10 border-white/30 text-white text-sm"
                            placeholder="#1e40af"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Via Color</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={pageEffects.backgroundGradient.via}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              backgroundGradient: {
                                ...prev.backgroundGradient,
                                via: e.target.value
                              }
                            }))}
                            className="w-12 h-8 rounded border border-white/20 bg-transparent"
                          />
                          <Input
                            type="text"
                            value={pageEffects.backgroundGradient.via}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              backgroundGradient: {
                                ...prev.backgroundGradient,
                                via: e.target.value
                              }
                            }))}
                            className="bg-white/10 border-white/30 text-white text-sm"
                            placeholder="#7c3aed"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">To Color</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={pageEffects.backgroundGradient.to}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              backgroundGradient: {
                                ...prev.backgroundGradient,
                                to: e.target.value
                              }
                            }))}
                            className="w-12 h-8 rounded border border-white/20 bg-transparent"
                          />
                          <Input
                            type="text"
                            value={pageEffects.backgroundGradient.to}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              backgroundGradient: {
                                ...prev.backgroundGradient,
                                to: e.target.value
                              }
                            }))}
                            className="bg-white/10 border-white/30 text-white text-sm"
                            placeholder="#059669"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div className="mt-4">
                      <Label className="text-white mb-2 block">Preview</Label>
                      <div
                        className="w-full h-20 rounded-lg border border-white/20"
                        style={{
                          background: `linear-gradient(135deg, ${pageEffects.backgroundGradient.from} 0%, ${pageEffects.backgroundGradient.via} 50%, ${pageEffects.backgroundGradient.to} 100%)`
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Tab Colors */}
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold text-lg">Tab Button Colors</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">Active Tab</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={pageEffects.tabColors.active}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              tabColors: {
                                ...prev.tabColors,
                                active: e.target.value
                              }
                            }))}
                            className="w-12 h-8 rounded border border-white/20 bg-transparent"
                          />
                          <Input
                            type="text"
                            value={pageEffects.tabColors.active}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              tabColors: {
                                ...prev.tabColors,
                                active: e.target.value
                              }
                            }))}
                            className="bg-white/10 border-white/30 text-white text-sm"
                            placeholder="#2563eb"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Inactive Tab</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={pageEffects.tabColors.inactive}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              tabColors: {
                                ...prev.tabColors,
                                inactive: e.target.value
                              }
                            }))}
                            className="w-12 h-8 rounded border border-white/20 bg-transparent"
                          />
                          <Input
                            type="text"
                            value={pageEffects.tabColors.inactive}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              tabColors: {
                                ...prev.tabColors,
                                inactive: e.target.value
                              }
                            }))}
                            className="bg-white/10 border-white/30 text-white text-sm"
                            placeholder="#374151"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Hover Color</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={pageEffects.tabColors.hover}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              tabColors: {
                                ...prev.tabColors,
                                hover: e.target.value
                              }
                            }))}
                            className="w-12 h-8 rounded border border-white/20 bg-transparent"
                          />
                          <Input
                            type="text"
                            value={pageEffects.tabColors.hover}
                            onChange={(e) => setPageEffects(prev => ({
                              ...prev,
                              tabColors: {
                                ...prev.tabColors,
                                hover: e.target.value
                              }
                            }))}
                            className="bg-white/10 border-white/30 text-white text-sm"
                            placeholder="#1d4ed8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold text-lg">Page Text Content</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-white mb-2 block">Landing Page Title</Label>
                        <Input
                          type="text"
                          value={pageEffects.textContent.landingTitle}
                          onChange={(e) => setPageEffects(prev => ({
                            ...prev,
                            textContent: {
                              ...prev.textContent,
                              landingTitle: e.target.value
                            }
                          }))}
                          className="bg-white/10 border-white/30 text-white"
                          placeholder="Your Complete Professional Solution"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Landing Page Subtitle</Label>
                        <Textarea
                          value={pageEffects.textContent.landingSubtitle}
                          onChange={(e) => setPageEffects(prev => ({
                            ...prev,
                            textContent: {
                              ...prev.textContent,
                              landingSubtitle: e.target.value
                            }
                          }))}
                          className="bg-white/10 border-white/30 text-white"
                          placeholder="Connect with certified professionals..."
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-white mb-2 block">Dashboard Welcome Message</Label>
                        <Input
                          type="text"
                          value={pageEffects.textContent.dashboardWelcome}
                          onChange={(e) => setPageEffects(prev => ({
                            ...prev,
                            textContent: {
                              ...prev.textContent,
                              dashboardWelcome: e.target.value
                            }
                          }))}
                          className="bg-white/10 border-white/30 text-white"
                          placeholder="Welcome to D.C. Technology Corp"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-white/20">
                    <Button
                      onClick={savePageEffects}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2"
                      disabled={pageEffectsLoading}
                    >
                      {pageEffectsLoading ? "Applying..." : "Apply Changes"}
                    </Button>
                    
                    <Button
                      onClick={resetPageEffects}
                      variant="outline"
                      className="border-white/50 text-white bg-white/10"
                      disabled={pageEffectsLoading}
                    >
                      {pageEffectsLoading ? "Resetting..." : "Reset to Defaults"}
                    </Button>
                    
                    <Button
                      onClick={() => {
                        // Preview changes logic
                        toast({
                          title: "Preview Mode",
                          description: "Changes will be visible in preview mode",
                        });
                      }}
                      variant="outline"
                      className="border-blue-400/50 text-blue-400 hover:bg-blue-400/10"
                    >
                      👁️ Preview Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Create Promo Code Modal */}
        {showCreatePromoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-bold text-white mb-4">Create Promo Code</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="promoCodeName" className="text-white">
                    Promo Code
                  </Label>
                  <Input
                    id="promoCodeName"
                    type="text"
                    placeholder="e.g., WELCOME30"
                    value={newPromoCode.code}
                    onChange={(e) => setNewPromoCode(prev => ({
                      ...prev,
                      code: e.target.value.toUpperCase()
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50"
                  />
                </div>
                
                <div>
                  <Label htmlFor="assignedEmail" className="text-white">
                    Assigned Email
                  </Label>
                  <Input
                    id="assignedEmail"
                    type="email"
                    placeholder="user@example.com"
                    value={newPromoCode.assignedEmail}
                    onChange={(e) => setNewPromoCode(prev => ({
                      ...prev,
                      assignedEmail: e.target.value
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50"
                  />
                </div>
                
                <div>
                  <Label htmlFor="trialDays" className="text-white">
                    Free Trial Days
                  </Label>
                  <Input
                    id="trialDays"
                    type="number"
                    min="1"
                    max="365"
                    value={newPromoCode.trialDurationDays}
                    onChange={(e) => setNewPromoCode(prev => ({
                      ...prev,
                      trialDurationDays: parseInt(e.target.value) || 30
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50"
                  />
                </div>
                
                <div>
                  <Label htmlFor="promoDescription" className="text-white">
                    Description (Optional)
                  </Label>
                  <Input
                    id="promoDescription"
                    type="text"
                    placeholder="Welcome bonus for new providers"
                    value={newPromoCode.description}
                    onChange={(e) => setNewPromoCode(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50"
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button
                  onClick={() => setShowCreatePromoModal(false)}
                  variant="outline"
                  className="flex-1 border-white/50 text-white bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createPromoCode}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!newPromoCode.code || !newPromoCode.assignedEmail}
                >
                  Create Promo Code
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Supervisor Modal */}
        {showCreateSupervisorModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-bold text-white mb-4">Create Supervisor</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="supervisorName" className="text-white">
                    Full Name *
                  </Label>
                  <Input
                    id="supervisorName"
                    type="text"
                    placeholder="Enter supervisor's full name"
                    value={newSupervisor.name}
                    onChange={(e) => setNewSupervisor(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="supervisorEmail" className="text-white">
                    Email Address *
                  </Label>
                  <Input
                    id="supervisorEmail"
                    type="email"
                    placeholder="supervisor@company.com"
                    value={newSupervisor.email}
                    onChange={(e) => setNewSupervisor(prev => ({
                      ...prev,
                      email: e.target.value
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="supervisorPassword" className="text-white">
                    Password *
                  </Label>
                  <Input
                    id="supervisorPassword"
                    type="password"
                    placeholder="Enter secure password"
                    value={newSupervisor.password}
                    onChange={(e) => setNewSupervisor(prev => ({
                      ...prev,
                      password: e.target.value
                    }))}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    minLength="8"
                    required
                  />
                  <p className="text-xs text-white/50 mt-1">Minimum 8 characters required</p>
                </div>
                
                <div>
                  <Label className="text-white mb-2 block">
                    Permissions
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {[
                      { key: 'view_users', label: 'View Users' },
                      { key: 'manage_users', label: 'Manage Users' },
                      { key: 'manage_promo_codes', label: 'Manage Promo Codes' },
                      { key: 'manage_advertisements', label: 'Manage Advertisements' },
                      { key: 'view_statistics', label: 'View Statistics' },
                      { key: 'export_data', label: 'Export Data' }
                    ].map(permission => (
                      <label key={permission.key} className="flex items-center space-x-2 text-white/80">
                        <input
                          type="checkbox"
                          checked={newSupervisor.permissions.includes(permission.key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewSupervisor(prev => ({
                                ...prev,
                                permissions: [...prev.permissions, permission.key]
                              }));
                            } else {
                              setNewSupervisor(prev => ({
                                ...prev,
                                permissions: prev.permissions.filter(p => p !== permission.key)
                              }));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{permission.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button
                  onClick={() => setShowCreateSupervisorModal(false)}
                  variant="outline"
                  className="flex-1 border-white/50 text-white bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createSupervisor}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!newSupervisor.name || !newSupervisor.email || !newSupervisor.password}
                >
                  Create Supervisor
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Supervisor Modal */}
        {showEditSupervisorModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-bold text-white mb-4">Edit Supervisor</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="editName" className="text-white">
                    Full Name
                  </Label>
                  <Input
                    id="editName"
                    type="text"
                    value={editSupervisorData.name}
                    onChange={(e) => setEditSupervisorData({...editSupervisorData, name: e.target.value})}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter supervisor's full name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="editEmail" className="text-white">
                    Email Address
                  </Label>
                  <Input
                    id="editEmail"
                    type="email"
                    value={editSupervisorData.email}
                    onChange={(e) => setEditSupervisorData({...editSupervisorData, email: e.target.value})}
                    className="bg-white/10 border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter supervisor's email"
                  />
                </div>
                
                <div>
                  <Label className="text-white mb-2 block">
                    Permissions
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {[
                      { key: 'view_users', label: 'View Users' },
                      { key: 'manage_users', label: 'Manage Users' },
                      { key: 'manage_promo_codes', label: 'Manage Promo Codes' },
                      { key: 'manage_advertisements', label: 'Manage Advertisements' },
                      { key: 'view_statistics', label: 'View Statistics' },
                      { key: 'export_data', label: 'Export Data' }
                    ].map(permission => (
                      <label key={permission.key} className="flex items-center space-x-2 text-white/80">
                        <input
                          type="checkbox"
                          checked={editSupervisorData.permissions.includes(permission.key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditSupervisorData({
                                ...editSupervisorData,
                                permissions: [...editSupervisorData.permissions, permission.key]
                              });
                            } else {
                              setEditSupervisorData({
                                ...editSupervisorData,
                                permissions: editSupervisorData.permissions.filter(p => p !== permission.key)
                              });
                            }
                          }}
                          className="rounded border-white/30 bg-white/10 text-blue-500 focus:ring-blue-500"
                        />
                        <span>{permission.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button
                  onClick={() => {
                    setShowEditSupervisorModal(false);
                    setEditingSupervisor(null);
                    setEditSupervisorData({
                      name: '',
                      email: '',
                      permissions: []
                    });
                  }}
                  variant="outline"
                  className="flex-1 border-white/50 text-white bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={updateSupervisor}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  disabled={!editSupervisorData.name || !editSupervisorData.email}
                >
                  Update Supervisor
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;